import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { InfoCard, TableColumn } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Tooltip,
  Typography,
} from '@material-ui/core';

// Must match ANNOTATION_PREFIX in the backend's ServiceTierProcessor.
const ANNOTATION_PREFIX = 'atg-digital.com/';
const NOT_SET = 'Not set';

const annotation = (entity: Entity, name: string) =>
  entity.metadata.annotations?.[`${ANNOTATION_PREFIX}${name}`];

/** The entity's service tier, as the backend resolved it from the quiz answer or the tier:N tag. */
export const entityTier = (entity: Entity) => annotation(entity, 'tier');

/** Whether the entity handles payments or PII, as the backend resolved it from the quiz answer or the processes: tag. */
export const entityProcesses = (entity: Entity) => ({
  payments: annotation(entity, 'processes-payments') === 'true',
  pii: annotation(entity, 'processes-pii') === 'true',
});

const label = (labels: Record<string, string>) => (value?: string) =>
  labels[value ?? ''] ?? NOT_SET;
const yesNo = label({ true: 'Yes', false: 'No' });
const pciOutOfScope = label({
  yes: 'Yes',
  no: 'No',
  unconfirmed: 'Not confirmed',
});
const YES_NO_UNKNOWN = { true: 'Yes', false: 'No', unknown: "Don't know" };
const yesNoUnknown = label(YES_NO_UNKNOWN);
const yesNoUnknownOrNotApplicable = label({
  ...YES_NO_UNKNOWN,
  'not-applicable': 'Not applicable',
});
const gdprFindings = label({
  addressed: 'Addressed',
  'risk-accepted': 'Risk accepted',
  outstanding: 'Still outstanding',
  'not-applicable': 'Not applicable',
});
const dataReach = label({
  none: 'None',
  'own-data': 'Only its own non-sensitive data',
  'customer-pii': 'Customer personal data (PII)',
  'payment-data': 'Payment data',
  'other-services': "Other services' data stores",
});
const tierLabel = (value?: string) => (value ? `Tier ${value}` : NOT_SET);
const text = (value?: string) => value ?? NOT_SET;
const gdprRating = label({
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'None',
  unknown: "Don't know",
});

type Field = [string, string, (value?: string) => string];

/** The quiz answers, grouped the way a reviewer reads them. */
const SECTIONS: [string, Field[]][] = [
  [
    'Business impact',
    [
      ['Blocks purchases', 'blocks-purchases', yesNo],
      ['Degrades experience', 'degrades-experience', yesNo],
      ['Nice-to-have', 'nice-to-have', yesNo],
      ['POC or internal', 'poc-or-internal', yesNo],
    ],
  ],
  [
    'Data',
    [
      ['Processes payments', 'processes-payments', yesNo],
      ['Out of PCI-DSS scope', 'pci-out-of-scope', pciOutOfScope],
      ['Processes PII', 'processes-pii', yesNo],
      ['Where PII is processed', 'pii-location', text],
      ['Personal data minimised', 'pii-minimised', yesNoUnknown],
      ['Retention policy', 'pii-retention-policy', yesNoUnknown],
      ['Kept out of logs', 'pii-free-logs', yesNoUnknown],
      ['GDPR review rating', 'gdpr-rating', gdprRating],
      ['GDPR findings', 'gdpr-findings-addressed', gdprFindings],
    ],
  ],
  [
    'Exposure',
    [
      ['Internet-facing', 'internet-facing', yesNoUnknown],
      [
        'Requires authentication',
        'requires-authentication',
        yesNoUnknownOrNotApplicable,
      ],
      ['Access control applied', 'access-control', yesNoUnknownOrNotApplicable],
      ['Encrypted at rest', 'encryption-at-rest', yesNoUnknown],
      ['Encrypted in transit', 'encryption-in-transit', yesNoUnknown],
    ],
  ],
  [
    'Operations',
    [['Dependencies documented', 'dependencies-documented', yesNoUnknown]],
  ],
  [
    'If compromised',
    [
      ['Data it could reach', 'compromise-data-reach', dataReach],
      ['Prod write or admin', 'compromise-prod-write', yesNoUnknown],
      ['Holds credentials', 'compromise-holds-credentials', yesNoUnknown],
      ['Worst case', 'compromise-worst-case', text],
    ],
  ],
];

const setTierLink = (entity: Entity) =>
  `/create/templates/default/set-service-tier?formData=${encodeURIComponent(
    JSON.stringify({ component: stringifyEntityRef(entity) }),
  )}`;

const hasAnswers = (entity: Entity) =>
  Object.keys(entity.metadata.annotations ?? {}).some(key =>
    key.startsWith(ANNOTATION_PREFIX),
  );

/** Follow-up questions the quiz only asks after a particular answer. */
const ONLY_WHEN: Record<string, [string, string]> = {
  'pii-location': ['processes-pii', 'true'],
  'pii-minimised': ['processes-pii', 'true'],
  'pii-retention-policy': ['processes-pii', 'true'],
  'pci-out-of-scope': ['processes-payments', 'true'],
};
const OPTIONAL = new Set(['compromise-worst-case']);

const applies = (entity: Entity, name: string) => {
  const condition = ONLY_WHEN[name];
  return !condition || annotation(entity, condition[0]) === condition[1];
};

/** Questions the quiz asks this entity, given its answers so far. */
const applicableQuestions = (entity: Entity) =>
  [
    'tier',
    ...SECTIONS.flatMap(([, fields]) => fields.map(([, name]) => name)),
  ].filter(name => applies(entity, name) && !OPTIONAL.has(name));

/** Questions that apply to this entity and have no answer yet. */
const unanswered = (entity: Entity) =>
  applicableQuestions(entity).filter(
    name => annotation(entity, name) === undefined,
  );

/** Tier, how much of the assessment is answered, and the way to finish it. */
const Progress = ({ entity, missing }: { entity: Entity; missing: number }) => {
  const tier = annotation(entity, 'tier');
  const total = applicableQuestions(entity).length;
  const answered = total - missing;
  return (
    <Box mb={2}>
      <Box display="flex" alignItems="center" mb={1}>
        <Chip
          color={tier ? 'primary' : 'default'}
          label={tierLabel(tier)}
          style={{ fontWeight: 600 }}
        />
        <Box flexGrow={1} ml={2}>
          <Typography variant="body2" color="textSecondary">
            {missing === 0
              ? 'Assessment complete'
              : `${answered} of ${total} questions answered`}
          </Typography>
        </Box>
        {missing > 0 && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            href={setTierLink(entity)}
          >
            Complete assessment
          </Button>
        )}
      </Box>
      <LinearProgress
        variant="determinate"
        value={total ? (answered / total) * 100 : 0}
      />
    </Box>
  );
};

/**
 * One group of answers on a single row: answers as chips, and how many are
 * still open, with their names on hover rather than spelled out.
 */
const Section = ({ title, fields }: { title: string; fields: Field[] }) => {
  const { entity } = useEntity();
  const shown = fields.filter(([, name]) => applies(entity, name));
  if (shown.length === 0) return null;
  const answered = shown.filter(([, name]) => annotation(entity, name));
  const open = shown.filter(
    ([, name]) => !annotation(entity, name) && !OPTIONAL.has(name),
  );
  return (
    <Box
      display="flex"
      alignItems="baseline"
      py={1.25}
      borderTop="1px solid rgba(0, 0, 0, 0.08)"
    >
      <Box width={140} flexShrink={0}>
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        style={{ gap: 6 }}
      >
        {answered.map(([fieldLabel, name, format]) => (
          <Chip
            key={name}
            size="small"
            variant="outlined"
            label={`${fieldLabel}: ${format(annotation(entity, name))}`}
          />
        ))}
        {open.length > 0 && (
          <Tooltip title={open.map(([fieldLabel]) => fieldLabel).join(', ')}>
            <Typography variant="body2" color="textSecondary">
              {open.length} unanswered
            </Typography>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

/** Shown until the quiz has recorded anything, instead of a grid of "Not set". */
const NotAssessed = ({ entity }: { entity: Entity }) => (
  <Box textAlign="center" py={3}>
    <Typography variant="h6" gutterBottom>
      No tier set yet
    </Typography>
    <Typography color="textSecondary" paragraph>
      Answer a few questions about business impact, the data it handles and how
      it is exposed. It takes about five minutes and opens a pull request with
      the result.
    </Typography>
    <Button variant="contained" color="primary" href={setTierLink(entity)}>
      Set service tier
    </Button>
  </Box>
);

export const EntityServiceTierCard = () => {
  const { entity } = useEntity();
  const missing = unanswered(entity);
  if (!hasAnswers(entity)) {
    return (
      <InfoCard title="Service tier and risk">
        <NotAssessed entity={entity} />
      </InfoCard>
    );
  }
  return (
    <InfoCard
      title="Service tier and risk"
      deepLink={
        missing.length === 0
          ? { title: 'Update answers', link: setTierLink(entity) }
          : undefined
      }
    >
      <Progress entity={entity} missing={missing.length} />
      {SECTIONS.map(([title, fields]) => (
        <Section key={title} title={title} fields={fields} />
      ))}
    </InfoCard>
  );
};

const columnLabel = (entity: Entity) => {
  const tier = annotation(entity, 'tier');
  return tier ? `T${tier}` : NOT_SET;
};

export const tierColumn: TableColumn<CatalogTableRow> = {
  title: 'Tier',
  width: 'auto',
  // Untiered rows sort last in ascending order, so tier 1 comes first.
  customSort: (a, b) =>
    (annotation(a.entity, 'tier') ?? '9').localeCompare(
      annotation(b.entity, 'tier') ?? '9',
    ),
  customFilterAndSearch: (term, { entity }) =>
    columnLabel(entity).toLowerCase().includes(String(term).toLowerCase()),
  render: ({ entity }) =>
    annotation(entity, 'tier') ? columnLabel(entity) : <em>{NOT_SET}</em>,
};
